import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  Contact,
  ContactCounts,
  ContactImportCommitResult,
  ContactImportDuplicateAction,
  ContactImportPreviewResult,
  ContactListItem,
  ContactListQuery,
  CreateContactInput,
  UpdateContactInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['contacts'] as const;

export interface ContactPage {
  items: ContactListItem[];
  meta: CollectionMeta;
}

function toQuery(q: ContactListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    search: q.search || undefined,
    status: q.status,
    source: q.source || undefined,
    tagId: q.tagId || undefined,
    name: q.name || undefined,
    company: q.company || undefined,
    email: q.email || undefined,
    phone: q.phone || undefined,
    updatedFrom: q.updatedFrom,
    updatedTo: q.updatedTo,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listContacts(q: ContactListQuery): Promise<ContactPage> {
  const res = await apiClient.getCollection<ContactListItem>('/contacts', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}

export const getContact = (id: string) => apiClient.get<Contact>(`/contacts/${id}`);
export const createContact = (input: CreateContactInput) =>
  apiClient.post<Contact>('/contacts', input);
export const updateContact = (id: string, patch: UpdateContactInput) =>
  apiClient.patch<Contact>(`/contacts/${id}`, patch);
export const deleteContact = (id: string) => apiClient.delete<void>(`/contacts/${id}`);

export function useContacts(query: ContactListQuery) {
  return useQuery<ContactPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listContacts(query),
    placeholderData: keepPreviousData,
  });
}

/** All / per-status counts for the grid tabs, under the same filters (status ignored). */
export function useContactCounts(query: ContactListQuery) {
  const { page: _p, pageSize: _ps, sortBy: _s, sortOrder: _o, status: _st, ...filters } = query;
  return useQuery<ContactCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () => apiClient.get<ContactCounts>('/contacts/counts', { query: toQuery(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | undefined) {
  return useQuery<Contact, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getContact(id!),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation<Contact, ApiClientError, CreateContactInput>({
    mutationFn: createContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateContact(id: string) {
  const qc = useQueryClient();
  return useMutation<Contact, ApiClientError, UpdateContactInput>({
    mutationFn: (patch) => updateContact(id, patch),
    onSuccess: (contact) => {
      qc.setQueryData([...KEY, 'detail', id], contact);
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Downloads the bulk-import .xlsx template and triggers the browser's save-file flow. */
export async function downloadContactImportTemplate(): Promise<void> {
  const { blob, fileName } = await apiClient.getBlob('/contacts/import/template');
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? 'contacts-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const previewContactImport = (file: File) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiClient.post<ContactImportPreviewResult>('/contacts/import/preview', form);
};

export const commitContactImport = (file: File, duplicateAction: ContactImportDuplicateAction) => {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('duplicateAction', duplicateAction);
  return apiClient.post<ContactImportCommitResult>('/contacts/import', form);
};

export function usePreviewContactImport() {
  return useMutation<ContactImportPreviewResult, ApiClientError, File>({
    mutationFn: previewContactImport,
  });
}

export function useCommitContactImport() {
  const qc = useQueryClient();
  return useMutation<
    ContactImportCommitResult,
    ApiClientError,
    { file: File; duplicateAction: ContactImportDuplicateAction }
  >({
    mutationFn: ({ file, duplicateAction }) => commitContactImport(file, duplicateAction),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
